import { supabase } from './supabase';

// Columnas de la plantilla (en este orden)
export const TEMPLATE_HEADERS = ['Nombre completo', 'Email', 'Teléfono', 'DNI', 'Fecha de nacimiento', 'Fecha de ingreso', 'Puesto', 'Sede', 'Área', 'Tipo de pago', 'Sueldo mensual', 'Valor hora', 'Viático por día', 'Días de trabajo', 'Entrada', 'Salida', 'Contraseña (opcional)'];
const EXAMPLES = [
  ['Ana Pérez', 'ana@ejemplo.com', '1155550000', '30111222', '15/04/1990', '01/03/2025', 'Recepcionista', 'Sede Centro | Sede Norte', '', 'Mensual', '450000', '', '', 'L-V', '09:00', '17:00', ''],
  ['Luis Gómez', 'luis@ejemplo.com', '', '', '', '10/06/2025', 'Mozo', 'Sede Centro', 'Salón', 'Por hora', '', '8500', '5000', '', '', '', ''],
];
const KEYS = ['name', 'email', 'phone', 'document_id', 'birth_date', 'hire_date', 'job_title', 'location', 'area', 'pay_type', 'salary', 'hourly_rate', 'per_diem', 'days', 'start', 'end', 'password'];
const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
const HEAD_MAP = {
  name: ['nombre completo', 'nombre', 'nombre y apellido', 'apellido y nombre', 'empleado', 'persona'],
  email: ['email', 'mail', 'correo', 'correo electronico', 'e mail'],
  phone: ['telefono', 'tel', 'celular', 'whatsapp'],
  document_id: ['dni', 'documento', 'cuil', 'cuit'],
  birth_date: ['fecha de nacimiento', 'nacimiento', 'fecha nacimiento'],
  hire_date: ['fecha de ingreso', 'ingreso', 'fecha ingreso', 'alta'],
  job_title: ['puesto', 'cargo', 'funcion'],
  location: ['sede', 'sucursal', 'local'],
  area: ['area', 'sector', 'departamento'],
  pay_type: ['tipo de pago', 'pago', 'modalidad', 'tipo'],
  salary: ['sueldo mensual', 'sueldo', 'salario', 'sueldo basico', 'basico'],
  hourly_rate: ['valor hora', 'valor por hora', 'precio hora', 'hora'],
  per_diem: ['viatico por dia', 'viatico', 'viaticos'],
  days: ['dias de trabajo', 'dias', 'dias laborales'],
  start: ['entrada', 'hora de entrada', 'ingreso horario', 'desde'],
  end: ['salida', 'hora de salida', 'hasta'],
  password: ['contrasena opcional', 'contrasena', 'clave', 'password'],
};

export const templateCsv = () => {
  const line = (a) => a.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';');
  return '﻿' + [TEMPLATE_HEADERS, ...EXAMPLES].map(line).join('\r\n');
};
export const downloadTemplate = () => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([templateCsv()], { type: 'text/csv;charset=utf-8' }));
  a.download = 'plantilla-personas.csv'; a.click();
};

// CSV con separador ; , o tab (Excel en español usa ;), con comillas
const parseCsv = (text) => {
  text = text.replace(/^﻿/, '');
  const first = text.split(/\r?\n/, 1)[0] || '';
  const sep = [';', '\t', ','].map((s) => [s, first.split(s).length]).sort((a, b) => b[1] - a[1])[0][0];
  const rows = []; let row = []; let cur = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
    else if (c === '"') q = true;
    else if (c === sep) { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(cur); rows.push(row); row = []; cur = ''; }
    else cur += c;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows;
};

// Devuelve una matriz de filas (la primera es el encabezado)
export const readSheet = async (file) => {
  if (file.size > 5 * 1024 * 1024) throw new Error('El archivo es demasiado grande (máximo 5 MB).');
  if (/\.xlsx$/i.test(file.name)) {
    const { default: readXlsx } = await import('read-excel-file');
    return readXlsx(file);
  }
  if (/\.(csv|txt)$/i.test(file.name)) return parseCsv(await file.text());
  throw new Error('Subí un archivo Excel (.xlsx) o CSV. Si tenés un .xls, guardalo como .xlsx.');
};

const pad = (n) => String(n).padStart(2, '0');
const validYMD = (y, m, d) => { const t = new Date(Date.UTC(y, m - 1, d)); return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d; };
const toDate = (v) => {
  if (v == null || v === '') return { v: null };
  if (v instanceof Date) return { v: `${v.getUTCFullYear()}-${pad(v.getUTCMonth() + 1)}-${pad(v.getUTCDate())}` };
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m && validYMD(+m[1], +m[2], +m[3])) return { v: `${m[1]}-${pad(m[2])}-${pad(m[3])}` };
  m = s.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2}|\d{4})$/);
  if (m) { let y = +m[3]; if (y < 100) y += y > 40 ? 1900 : 2000; if (validYMD(y, +m[2], +m[1])) return { v: `${y}-${pad(m[2])}-${pad(m[1])}` }; }
  return { err: `La fecha "${s}" no es válida (usá dd/mm/aaaa)` };
};
const toTime = (v) => {
  if (v == null || v === '') return { v: null };
  if (v instanceof Date) return { v: `${pad(v.getUTCHours())}:${pad(v.getUTCMinutes())}` };
  if (typeof v === 'number' && v > 0 && v < 1) { const mins = Math.round(v * 1440); return { v: `${pad(Math.floor(mins / 60) % 24)}:${pad(mins % 60)}` }; }
  const m = String(v).trim().match(/^(\d{1,2})(?:[:.h](\d{2}))?\s*(?:hs?)?$/i);
  if (m && +m[1] < 24 && +(m[2] || 0) < 60) return { v: `${pad(m[1])}:${m[2] || '00'}` };
  return { err: `La hora "${v}" no es válida (usá hh:mm)` };
};
const toMoney = (v) => {
  if (v == null || v === '') return { v: null };
  if (typeof v === 'number') return v >= 0 ? { v } : { err: 'El monto no puede ser negativo' };
  let s = String(v).replace(/[^\d.,-]/g, '');
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
  else if (s.includes(',')) s = s.replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? { v: n } : { err: `El monto "${v}" no es válido` };
};
const DAY_TOKENS = { l: 1, lu: 1, lun: 1, lunes: 1, m: 2, ma: 2, mar: 2, martes: 2, x: 3, mi: 3, mie: 3, miercoles: 3, j: 4, ju: 4, jue: 4, jueves: 4, v: 5, vi: 5, vie: 5, viernes: 5, s: 6, sa: 6, sab: 6, sabado: 6, d: 0, do: 0, dom: 0, domingo: 0 };
const toDays = (v) => {
  const s = norm(v);
  if (!s) return { v: [] };
  if (/^(todos|todos los dias|toda la semana|lun a dom)$/.test(s)) return { v: [0, 1, 2, 3, 4, 5, 6] };
  const r = s.match(/^([a-z]+)\s*(?:-|a|al)\s*([a-z]+)$/) || norm(String(v).replace(/-/g, ' a ')).match(/^([a-z]+) a ([a-z]+)$/);
  if (r && DAY_TOKENS[r[1]] !== undefined && DAY_TOKENS[r[2]] !== undefined) {
    const out = []; let d = DAY_TOKENS[r[1]]; const end = DAY_TOKENS[r[2]];
    for (let i = 0; i < 7; i++) { out.push(d); if (d === end) break; d = (d + 1) % 7; }
    return { v: out };
  }
  const toks = s.split(/[\s,;/y]+/).filter(Boolean);
  const out = [];
  for (const t of toks) {
    if (/^[0-6]$/.test(t)) out.push(Number(t)); else if (DAY_TOKENS[t] !== undefined) out.push(DAY_TOKENS[t]); else return { err: `Los días "${v}" no se entienden (ej.: L-V o L M X J V)` };
  }
  return { v: [...new Set(out)] };
};
const toPayType = (v, hasSalary, hasRate) => {
  const s = norm(v);
  if (!s) return hasRate && !hasSalary ? 'hourly' : 'fixed';
  if (/hora|jornal|hourly|horas/.test(s)) return 'hourly';
  if (/mensual|fijo|sueldo|fixed|mes/.test(s)) return 'fixed';
  return null;
};

// ctx: { locations:[{name}], areas:[], existingEmails:[] }
export const buildRows = (matrix, ctx) => {
  if (!matrix || matrix.length < 2) throw new Error('El archivo no tiene filas para importar.');
  const head = matrix[0].map(norm);
  const col = {};
  KEYS.forEach((k) => { const i = head.findIndex((h) => HEAD_MAP[k].includes(h)); if (i >= 0) col[k] = i; });
  if (col.name === undefined || col.email === undefined) throw new Error('No encontré las columnas «Nombre completo» y «Email». Usá la plantilla.');
  const locs = new Map((ctx.locations || []).map((l) => [norm(l.name), l.name]));
  const areas = new Set((ctx.areas || []).map(norm));
  const existing = new Set((ctx.existingEmails || []).map((e) => String(e).toLowerCase()));
  const seen = new Map();
  const out = [];
  for (let r = 1; r < matrix.length; r++) {
    const line = matrix[r];
    if (!line || line.every((c) => c == null || String(c).trim() === '')) continue;
    const get = (k) => (col[k] === undefined ? null : line[col[k]] ?? null);
    const str = (k) => { const v = get(k); return v == null ? '' : (v instanceof Date ? '' : String(v).trim()); };
    const errors = []; const warnings = [];
    const take = (res) => { if (res.err) errors.push(res.err); return res.v ?? null; };
    const row = { row: r + 1, name: str('name'), email: str('email').toLowerCase(), phone: str('phone'), document_id: str('document_id').replace(/[.\s]/g, ''), job_title: str('job_title'), area: str('area'), password: str('password') };
    row.birth_date = take(toDate(get('birth_date'))); row.hire_date = take(toDate(get('hire_date')));
    row.salary = take(toMoney(get('salary'))); row.hourly_rate = take(toMoney(get('hourly_rate'))); row.per_diem = take(toMoney(get('per_diem')));
    row.start = take(toTime(get('start'))); row.end = take(toTime(get('end'))); row.days = take(toDays(get('days'))) || [];
    const pt = toPayType(get('pay_type'), row.salary > 0, row.hourly_rate > 0);
    if (pt === null) errors.push(`El tipo de pago "${str('pay_type')}" no se entiende (Mensual o Por hora)`); row.pay_type = pt || 'fixed';
    if (row.name.length < 2) errors.push('Falta el nombre');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(row.email)) errors.push('El email no es válido');
    else if (existing.has(row.email)) errors.push('Ese email ya existe en tu organización');
    else if (seen.has(row.email)) errors.push(`Email repetido (también en la fila ${seen.get(row.email)})`);
    else seen.set(row.email, r + 1);
    if (row.password && row.password.length < 6) errors.push('La contraseña debe tener al menos 6 caracteres');
    const locList = str('location').split(/[|;/]+/).map((x) => x.trim()).filter(Boolean);
    const okLocs = [];
    locList.forEach((l) => { if (locs.has(norm(l))) okLocs.push(locs.get(norm(l))); else errors.push(`La sede "${l}" no existe`); });
    row.location = okLocs[0] || ''; row.locations = [...new Set(okLocs)];
    if (row.area && ctx.areas?.length && !areas.has(norm(row.area))) warnings.push(`El área "${row.area}" no está en tu lista de áreas`);
    if (row.pay_type === 'fixed' && !(row.salary > 0)) warnings.push('Sin sueldo: lo cargás después en su ficha');
    if (row.pay_type === 'hourly' && !(row.hourly_rate > 0)) warnings.push('Sin valor hora: usará el de su área o el general');
    if (row.pay_type === 'fixed' && row.days.length && (!row.start || !row.end)) warnings.push('Sin horario de entrada/salida: se carga 09:00 a 17:00');
    if (row.pay_type === 'fixed' && !row.days.length) warnings.push('Sin días de trabajo: lo configurás después en su ficha');
    row.errors = errors; row.warnings = warnings;
    out.push(row);
  }
  if (!out.length) throw new Error('No encontré filas con datos.');
  if (out.length > 300) throw new Error(`El archivo tiene ${out.length} filas; el máximo por importación es 300. Dividilo en partes.`);
  return out;
};

// Envía en lotes de 50. onProgress(hechos, total)
export const importRows = async (rows, onProgress) => {
  const results = [];
  for (let i = 0; i < rows.length; i += 50) {
    const part = rows.slice(i, i + 50).map((r) => ({ row: r.row, name: r.name, email: r.email, password: r.password || null, phone: r.phone, document_id: r.document_id, birth_date: r.birth_date, hire_date: r.hire_date, job_title: r.job_title, location: r.location, locations: r.locations, area: r.area, pay_type: r.pay_type, salary: r.salary, hourly_rate: r.hourly_rate, per_diem: r.per_diem, days: r.days, start: r.start, end: r.end }));
    const { data, error } = await supabase.rpc('admin_bulk_create_employees', { p_rows: part });
    if (error) throw new Error(error.message);
    results.push(...data);
    onProgress?.(Math.min(i + 50, rows.length), rows.length);
  }
  return results;
};

export const downloadCsv = (name, lines) => {
  const body = lines.map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' }));
  a.download = name; a.click();
};
